/**
 * Page /scolaire-cantine — espace famille SCOLAIRE (socle partagé).
 *
 * L'enfant est une fiche unique. À la création/édition, on coche les services
 * souhaités (Cantine / Périscolaire / Centre de loisirs). Cocher « Cantine »
 * ouvre l'onglet réservation cantine pour cet enfant — sans jamais re-saisir
 * l'enfant ailleurs.
 *
 * R13 : aucun window.alert/confirm/prompt — uniquement le composant Dialog.
 * Données sensibles (allergènes/PAI/régime) déclarées ici ; modifiables ensuite
 * uniquement par la mairie (le backend renvoie 403 sinon).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { MesRepasAdultePanel } from '@/components/famille/MesRepasAdultePanel';
import { useModules } from '@/lib/modules-context';
import { Card, Button, Input, Field, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AppHeader } from '@/components/AppHeader';
import { MenusSemaineEncart } from '@/components/scolaire/MenusSemaineEncart';
import { MesFacturesEncart } from '@/components/scolaire/MesFacturesEncart';
import { QuotientFamilialEncart } from '@/components/scolaire/QuotientFamilialEncart';
import { PresenceCalendar } from '@/components/scolaire/PresenceCalendar';
import { PetiteEnfanceEspaceSections } from '@/components/petite-enfance/PetiteEnfanceEspaceSections';
import { MesPiecesEnfance } from '@/components/famille/MesPiecesEnfance';
import { JustificatifDepotFamille } from '@/components/famille/JustificatifDepotFamille';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { Landmark, ArrowLeft, GraduationCap, Plus, Users, Baby, AlertTriangle, Utensils, CalendarDays, Clock, Sun } from 'lucide-react';
import { ALLERGENES_INCO, PAI_STATUTS, REGIMES_ALIMENTAIRES } from '@roronoa-collectivites/shared-types';
import type {
  ScolaireFamille, ScolaireFamilleDetail, ScolaireEnfant, InscriptionStatut, ScolaireEcole,
  AllergeneInco, PaiStatut, RegimeAlimentaire, CantineReservation,
  DossierScolaireDetail,
} from '@roronoa-collectivites/shared-types';

export function ScolaireCantinePage() {
  const { user } = useAuth();
  const { isModuleActif } = useModules();
  // Mode parent (espace citoyen) si :
  //  - administré (citoyen) ; OU
  //  - membre du staff rattaché à un AUTRE service que scolaire_cantine
  //    (ex. agent associations) → il accède ici en tant que parent.
  // Le staff scolaire (manager/agent service scolaire_cantine) + admin/maire
  // voient la vue de gestion « Familles inscrites ».
  const isStaffScolaire =
    user?.role === 'super_admin' ||
    user?.role === 'admin' ||
    ((user?.role === 'manager' || user?.role === 'agent') && user?.service === 'scolaire_cantine');
  const isParent = !isStaffScolaire;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [familles, setFamilles] = useState<ScolaireFamille[]>([]);
  const [detail, setDetail] = useState<ScolaireFamilleDetail | null>(null);
  const [familleDialog, setFamilleDialog] = useState(false);
  const [enfantDialog, setEnfantDialog] = useState(false);
  const [inscriptionStatut, setInscriptionStatut] = useState<InscriptionStatut | null>(null);
  const [etablissements, setEtablissements] = useState<ScolaireEcole[]>([]);

  async function load() {
    setLoading(true); setError(null);
    try {
      const list = await api.scolaire.listFamilles();
      setFamilles(list);
      if (isParent && list[0]) setDetail(await api.scolaire.getFamille(list[0].id));
      try { setInscriptionStatut(await api.scolaire.statutInscription()); } catch { /* non bloquant */ }
      try { setEtablissements(await api.scolaire.listEtablissementsHoraires()); } catch { /* non bloquant */ }
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Erreur de chargement');
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function openFamille(id: string) {
    try { setDetail(await api.scolaire.getFamille(id)); }
    catch (e) { setError(e instanceof ApiException ? e.payload.error : 'Erreur'); }
  }

  return (
    <div className="min-h-screen bg-canvas">
      <AppHeader module="Scolaire & cantine" accent="cantine" />

      <main className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-wash text-accent">
            <GraduationCap className="h-6 w-6" />
          </span>
          <div>
            <SectionEyebrow>{isParent ? 'Scolaire · Cantine · Petite enfance' : 'Restauration scolaire'}</SectionEyebrow>
            <h1 className="font-display text-2xl font-bold text-ink">
              {isParent ? 'Ma famille & mes services enfance' : 'Familles inscrites'}
            </h1>
          </div>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {loading && <p className="text-soft">Chargement…</p>}

        {isParent && <MenusSemaineEncart />}

        {!loading && (
          <>
            {isParent && familles.length === 0 && (
              <Card className="p-6 text-center">
                <Baby className="mx-auto mb-3 h-10 w-10 text-soft" />
                <h2 className="mb-1 font-display text-lg font-semibold text-ink">Créez votre dossier famille</h2>
                <p className="mb-4 text-sm text-soft">
                  Renseignez votre famille puis ajoutez vos enfants. Vous accéderez ensuite à tous les
                  services selon leur âge : cantine, périscolaire, centre de loisirs et petite enfance
                  (crèche, accueil). Vos pièces ne sont à déposer qu'une seule fois.
                </p>
                <Button onClick={() => setFamilleDialog(true)}>
                  <Plus className="mr-1.5 h-4 w-4" /> Créer ma famille
                </Button>
              </Card>
            )}

            {isParent && detail && (
              <>
                <QuotientFamilialEncart familleId={detail.id} quotientFamilial={detail.quotientFamilial} qfSource={detail.qfSource} />
                <MesFacturesEncart familleId={detail.id} title="Mes factures — scolaire / cantine" excludeService="petite_enfance" />
                <DossierInscriptionCard familleId={detail.id} nbEnfants={detail.enfants.length} />
                {/* Liste UNIFIÉE des pièces : une seule liste, pièces réellement
                    demandées (réglées en GRC) selon l'usage réel de la famille. */}
                <SectionRepliable title="Mes pièces justificatives">
                  <p className="mb-3 text-xs text-soft">Les pièces demandées par votre mairie. Une pièce déposée sert tous vos dossiers (scolaire, cantine, petite enfance).</p>
                  <MesPiecesEnfance familleId={detail.id} setError={setError} />
                </SectionRepliable>
                {/* Item 12.3, geste famille (recette 20/08) : justifier une
                    absence en ligne. Le panneau se tait s'il n'y a ni absence
                    justifiable ni dépôt passé. */}
                <SectionRepliable title="Justificatifs d'absence">
                  <JustificatifDepotFamille />
                </SectionRepliable>
                <FamilleDetailCard detail={detail} statut={inscriptionStatut} onAddEnfant={() => setEnfantDialog(true)} onReload={() => openFamille(detail.id)} />

                {/* Items 12.5/12.6 — le personnel se déclare lui-même, ici,
                    exactement là où il inscrirait son enfant. Le panneau ne
                    s'affiche pas pour un habitant sans droit sur ce service. */}
                <div className="mt-8"><MesRepasAdultePanel /></div>

                {/* ── Services Petite enfance (affichés si le module est actif) ── */}
                {isModuleActif('petite_enfance') && (
                <section className="mt-8 rounded-r-2xl border border-cantine/25 bg-cantine-tint/15 p-4 sm:p-5">
                  <SectionRepliable
                    bare
                    className=""
                    defaultOpen={false}
                    header={
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-r-lg bg-cantine-tint text-cantine-fg">
                          <Baby className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div>
                          <SectionEyebrow as="div">Pour vos tout-petits</SectionEyebrow>
                          <h2 className="font-display text-lg font-semibold text-ink">Petite enfance — crèche, accueil, assistantes maternelles</h2>
                        </div>
                      </div>
                    }
                  >
                    <PetiteEnfanceEspaceSections setError={setError} embedded />
                  </SectionRepliable>
                </section>
                )}
              </>
            )}

            {!isParent && (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="flex items-center gap-1.5 text-sm text-soft"><Users className="h-4 w-4" /> {familles.length} famille(s)</p>
                  <Button variant="secondary" onClick={() => setFamilleDialog(true)}>
                    <Plus className="mr-1.5 h-4 w-4" /> Ajouter une famille
                  </Button>
                </div>
                {detail ? (
                  <>
                    <Button variant="ghost" className="mb-3" onClick={() => setDetail(null)}>
                      <ArrowLeft className="mr-1.5 h-4 w-4" /> Liste des familles
                    </Button>
                    <FamilleDetailCard detail={detail} statut={inscriptionStatut} onAddEnfant={() => setEnfantDialog(true)} onReload={() => openFamille(detail.id)} />

                {/* Items 12.5/12.6 — le personnel se déclare lui-même, ici,
                    exactement là où il inscrirait son enfant. Le panneau ne
                    s'affiche pas pour un habitant sans droit sur ce service. */}
                <div className="mt-8"><MesRepasAdultePanel /></div>
                  </>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {familles.map((f) => (
                      <Card key={f.id} className="cursor-pointer p-4 hover:border-accent" onClick={() => openFamille(f.id)}>
                        <p className="font-display font-semibold text-ink">{f.nom}</p>
                        <p className="text-sm text-soft">{f.ville ?? '—'}</p>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}

            {etablissements.some((e) => (e.type !== 'centre_loisirs' && ((e.horaires && (e.horaires.ouverture || e.horaires.finJournee)) || (e.garderie && (e.garderie.ouvertureMatin || e.garderie.fermetureSoir)))) || (e.type === 'centre_loisirs' && e.alsh && (e.alsh.ouverture || e.alsh.fermeture))) && (
              <Card className="mt-6 p-5">
                <h3 className="mb-3 font-display font-semibold text-ink">Horaires d'accueil</h3>
                <ul className="space-y-3">
                  {etablissements.filter((e) => e.type !== 'centre_loisirs' && ((e.horaires && (e.horaires.ouverture || e.horaires.finJournee)) || (e.garderie && (e.garderie.ouvertureMatin || e.garderie.fermetureSoir)))).map((e) => (
                    <li key={e.id} className="text-sm">
                      <span className="font-medium text-ink">{e.nom}</span>
                      {e.horaires && (e.horaires.ouverture || e.horaires.finJournee) && (
                        <div className="ml-2 text-soft">
                          École :{' '}
                          {e.horaires.ouverture && e.horaires.finMatin ? `${e.horaires.ouverture}–${e.horaires.finMatin}` : ''}
                          {e.horaires.repriseApresMidi && e.horaires.finJournee ? ` · ${e.horaires.repriseApresMidi}–${e.horaires.finJournee}` : ''}
                          {e.horaires.mercrediOuverture && e.horaires.mercrediFin ? ` · Mercredi ${e.horaires.mercrediOuverture}–${e.horaires.mercrediFin}` : ''}
                        </div>
                      )}
                      {e.garderie && (e.garderie.ouvertureMatin || e.garderie.fermetureSoir) && (
                        <div className="ml-2 text-soft">
                          Garderie :{' '}
                          {e.garderie.ouvertureMatin ? `dès ${e.garderie.ouvertureMatin}` : ''}
                          {e.garderie.fermetureSoir ? ` · jusqu'à ${e.garderie.fermetureSoir}` : ''}
                          {e.garderie.mercrediOuverture && e.garderie.mercrediFermeture ? ` · Mercredi ${e.garderie.mercrediOuverture}–${e.garderie.mercrediFermeture}` : ''}
                        </div>
                      )}
                    </li>
                  ))}
                  {etablissements.filter((e) => e.type === 'centre_loisirs' && e.alsh && (e.alsh.ouverture || e.alsh.fermeture)).map((e) => (
                    <li key={e.id} className="text-sm">
                      <span className="font-medium text-ink">{e.nom}</span>
                      <div className="ml-2 text-soft">
                        ALSH :{' '}
                        {e.alsh?.ouverture && e.alsh?.fermeture ? `${e.alsh.ouverture}–${e.alsh.fermeture}` : ''}
                        {e.alsh?.mercrediOuverture && e.alsh?.mercrediFermeture ? ` · Mercredi ${e.alsh.mercrediOuverture}–${e.alsh.mercrediFermeture}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </main>

      <CreateFamilleDialog
        open={familleDialog}
        onClose={() => setFamilleDialog(false)}
        onCreated={async (fam) => { setFamilleDialog(false); await load(); await openFamille(fam.id); }}
      />
      {detail && (
        <CreateEnfantDialog
          open={enfantDialog}
          familleId={detail.id}
          petiteEnfanceActive={isModuleActif('petite_enfance')}
          onClose={() => setEnfantDialog(false)}
          onCreated={async () => { setEnfantDialog(false); await openFamille(detail.id); }}
        />
      )}
    </div>
  );
}

// ============================================================================
function FamilleDetailCard({ detail, statut, onAddEnfant, onReload }: {
  detail: ScolaireFamilleDetail; statut: InscriptionStatut | null; onAddEnfant: () => void; onReload: () => void;
}) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h2 className="mb-1 font-display text-lg font-semibold text-ink">{detail.nom}</h2>
        <p className="text-sm text-soft">
          {[detail.adresse, detail.codePostal, detail.ville].filter(Boolean).join(', ') || 'Adresse non renseignée'}
        </p>
        {detail.quotientFamilial != null && (
          <p className="mt-1 text-sm text-soft">Quotient familial : <strong>{detail.quotientFamilial}</strong></p>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-ink">Enfants ({detail.enfants.length})</h3>
        <Button size="sm" onClick={onAddEnfant} disabled={statut != null && !statut.ouverte}>
          <Plus className="mr-1.5 h-4 w-4" /> Ajouter un enfant
        </Button>
      </div>
      {statut != null && !statut.ouverte && (
        <Alert variant="info" className="mt-2">
          {statut.raison === 'pas_encore'
            ? <>Les inscriptions ne sont pas encore ouvertes{statut.ouverture ? <> — ouverture le <strong>{new Date(statut.ouverture).toLocaleDateString('fr-FR')}</strong></> : ''}.</>
            : <>Les inscriptions sont closes{statut.limite ? <> depuis le <strong>{new Date(statut.limite).toLocaleDateString('fr-FR')}</strong></> : ''}. Pour un ajout exceptionnel, contactez la mairie.</>}
        </Alert>
      )}

      {detail.enfants.length === 0 ? (
        <p className="text-sm text-soft">Aucun enfant enregistré pour l'instant.</p>
      ) : (
        <div className="space-y-3">
          {detail.enfants.map((e) => <EnfantCard key={e.id} enfant={e} />)}
        </div>
      )}

      {detail.enfants.length > 0 && (
        <Alert variant="info">
          Les allergènes et le statut PAI sont déclarés à l'inscription. Pour toute modification
          ultérieure, contactez la mairie.
        </Alert>
      )}
    </div>
  );
}

function EnfantCard({ enfant }: { enfant: ScolaireEnfant }) {
  const regimeLabel = REGIMES_ALIMENTAIRES.find((r) => r.value === enfant.regime)?.label ?? enfant.regime;
  const paiLabel = PAI_STATUTS.find((p) => p.value === enfant.paiStatut)?.label ?? enfant.paiStatut;
  const [showResa, setShowResa] = useState(false);
  const [inscrDialog, setInscrDialog] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Services activables/désactivables à tout moment (état local synchronisé)
  const [cantineOn, setCantineOn] = useState(enfant.inscritCantine);
  const [periOn, setPeriOn] = useState(enfant.inscritPeriscolaire);
  const [svcBusy, setSvcBusy] = useState<string | null>(null);
  const [svcErr, setSvcErr] = useState<string | null>(null);

  async function toggleService(service: 'cantine' | 'periscolaire', next: boolean) {
    setSvcBusy(service); setSvcErr(null);
    try {
      await api.scolaire.updateEnfant(enfant.id, service === 'cantine' ? { inscritCantine: next } : { inscritPeriscolaire: next });
      if (service === 'cantine') setCantineOn(next); else setPeriOn(next);
    } catch (e) { setSvcErr(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSvcBusy(null); }
  }

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-display font-semibold text-ink">{enfant.prenom} {enfant.nom}</p>
          {enfant.dateNaissance && <p className="text-xs text-soft">Né(e) le {enfant.dateNaissance}</p>}
        </div>
        <Badge variant={enfant.paiStatut === 'non_concerne' ? 'neutral' : 'warning'}>PAI : {paiLabel}</Badge>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs text-soft"><Utensils className="h-3.5 w-3.5" /> {regimeLabel}</span>
        {enfant.allergenes.length > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-urgent">
            <AlertTriangle className="h-3.5 w-3.5" />
            {enfant.allergenes.map((a) => ALLERGENES_INCO.find((x) => x.value === a)?.label ?? a).join(', ')}
          </span>
        )}
      </div>
      {/* Services : toggles activables/désactivables à tout moment */}
      {svcErr && <Alert variant="error" className="mt-2">{svcErr}</Alert>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button type="button" disabled={svcBusy === 'cantine'} onClick={() => toggleService('cantine', !cantineOn)}
          className={['inline-flex h-8 items-center gap-1.5 rounded-r-pill px-3 text-xs font-medium transition-all',
            cantineOn ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60',
            svcBusy === 'cantine' ? 'opacity-60' : ''].join(' ')}>
          <Utensils className="h-3.5 w-3.5" /> Cantine {cantineOn ? '✓' : ''}
        </button>
        <button type="button" disabled={svcBusy === 'periscolaire'} onClick={() => toggleService('periscolaire', !periOn)}
          className={['inline-flex h-8 items-center gap-1.5 rounded-r-pill px-3 text-xs font-medium transition-all',
            periOn ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60',
            svcBusy === 'periscolaire' ? 'opacity-60' : ''].join(' ')}>
          <Clock className="h-3.5 w-3.5" /> Périscolaire {periOn ? '✓' : ''}
        </button>
      </div>
      <p className="mt-1 text-2xs text-ink-subtle">Activez ou désactivez un service à tout moment, puis inscrivez sur une période.</p>
      {/* Gestion des présences au jour le jour (cantine + périscolaire) */}
      {(cantineOn || periOn) && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={showResa ? 'secondary' : 'ghost'} onClick={() => setShowResa((v) => !v)}>
              <CalendarDays className="mr-1.5 h-4 w-4" /> {showResa ? 'Masquer les présences' : 'Gérer les présences'}
            </Button>
            <Button size="sm" variant="cantine" onClick={() => setInscrDialog(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Inscrire sur une période
            </Button>
          </div>
          {showResa && <div className="mt-3"><PresenceCalendar key={reloadKey} enfantId={enfant.id} /></div>}
        </div>
      )}
      <InscriptionDialog open={inscrDialog} enfantId={enfant.id} cantineOn={cantineOn} periOn={periOn} onClose={() => setInscrDialog(false)}
        onCreated={() => { setInscrDialog(false); setShowResa(true); setReloadKey((k) => k + 1); }} />
    </Card>
  );
}

// ============================================================================
const JOURS_LABELS: { iso: number; label: string }[] = [
  { iso: 1, label: 'Lun' }, { iso: 2, label: 'Mar' }, { iso: 3, label: 'Mer' },
  { iso: 4, label: 'Jeu' }, { iso: 5, label: 'Ven' },
];

// ============================================================================
function CreateFamilleDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: (f: ScolaireFamilleDetail) => void;
}) {
  const [nom, setNom] = useState(''); const [ville, setVille] = useState('');
  const [codePostal, setCodePostal] = useState(''); const [adresse, setAdresse] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!nom.trim()) { setErr('Le nom de la famille est requis.'); return; }
    setBusy(true); setErr(null);
    try {
      const fam = await api.scolaire.createFamille({
        nom: nom.trim(), ville: ville.trim() || undefined,
        codePostal: codePostal.trim() || undefined, adresse: adresse.trim() || undefined,
      });
      setNom(''); setVille(''); setCodePostal(''); setAdresse(''); onCreated(fam);
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Erreur lors de la création'); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Créer une famille"
      footer={<><Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button><Button onClick={submit} disabled={busy}>{busy ? 'Création…' : 'Créer'}</Button></>}>
      <div className="space-y-3">
        {err && <Alert variant="error">{err}</Alert>}
        <Field label="Nom de la famille" htmlFor="fam-nom" required>
          <Input id="fam-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Famille Martin" />
        </Field>
        <Field label="Adresse" htmlFor="fam-adr"><Input id="fam-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Code postal" htmlFor="fam-cp"><Input id="fam-cp" value={codePostal} onChange={(e) => setCodePostal(e.target.value)} /></Field>
          <div className="col-span-2"><Field label="Ville" htmlFor="fam-ville"><Input id="fam-ville" value={ville} onChange={(e) => setVille(e.target.value)} /></Field></div>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
function CreateEnfantDialog({ open, familleId, petiteEnfanceActive, onClose, onCreated }: {
  open: boolean; familleId: string; petiteEnfanceActive?: boolean; onClose: () => void; onCreated: () => void;
}) {
  const [prenom, setPrenom] = useState(''); const [nom, setNom] = useState('');
  const [dateNaissance, setDateNaissance] = useState('');
  const [allergenes, setAllergenes] = useState<AllergeneInco[]>([]);
  const [paiStatut, setPaiStatut] = useState<PaiStatut>('non_concerne');
  const [regime, setRegime] = useState<RegimeAlimentaire>('standard');
  // Cases d'activation des services
  const [inscritCantine, setInscritCantine] = useState(true);
  const [inscritPeriscolaire, setInscritPeriscolaire] = useState(false);
  const [inscritAlsh, setInscritAlsh] = useState(false);
  const [inscritPetiteEnfance, setInscritPetiteEnfance] = useState(false);
  // Petite enfance : info vaccinale facultative ('' = non renseigné).
  const [vaccins, setVaccins] = useState<'' | 'oui' | 'non'>('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

  function toggleAllergene(a: AllergeneInco) {
    setAllergenes((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  }
  async function submit() {
    if (!prenom.trim() || !nom.trim()) { setErr('Prénom et nom requis.'); return; }
    setBusy(true); setErr(null);
    try {
      await api.scolaire.createEnfant({
        familleId, prenom: prenom.trim(), nom: nom.trim(),
        dateNaissance: dateNaissance || undefined, allergenes, paiStatut, regime,
        inscritCantine, inscritPeriscolaire, inscritAlsh, inscritPetiteEnfance,
        vaccinationsAJour: inscritPetiteEnfance && vaccins !== '' ? vaccins === 'oui' : undefined,
      });
      onCreated();
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Erreur lors de l\'ajout'); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter un enfant"
      description="Renseignez l'enfant une seule fois, puis cochez les services souhaités. Les allergènes et le PAI ne pourront être modifiés ensuite que par la mairie."
      size="lg"
      footer={<><Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button><Button onClick={submit} disabled={busy}>{busy ? 'Ajout…' : 'Ajouter'}</Button></>}>
      <div className="space-y-3">
        {err && <Alert variant="error">{err}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom" htmlFor="enf-prenom" required><Input id="enf-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} /></Field>
          <Field label="Nom" htmlFor="enf-nom" required><Input id="enf-nom" value={nom} onChange={(e) => setNom(e.target.value)} /></Field>
        </div>
        <Field label="Date de naissance" htmlFor="enf-dob"><Input id="enf-dob" type="date" value={dateNaissance} onChange={(e) => setDateNaissance(e.target.value)} /></Field>

        <Field label="Régime alimentaire" htmlFor="enf-regime">
          <select id="enf-regime" value={regime} onChange={(e) => setRegime(e.target.value as RegimeAlimentaire)}
            className="w-full rounded-lg border border-hairline bg-paper px-3 py-2 text-sm text-ink">
            {REGIMES_ALIMENTAIRES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Statut PAI (projet d'accueil individualisé)" htmlFor="enf-pai">
          <select id="enf-pai" value={paiStatut} onChange={(e) => setPaiStatut(e.target.value as PaiStatut)}
            className="w-full rounded-lg border border-hairline bg-paper px-3 py-2 text-sm text-ink">
            {PAI_STATUTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>

        <Field label="Allergènes déclarés" htmlFor="enf-allergenes">
          <div id="enf-allergenes" className="flex flex-wrap gap-2">
            {ALLERGENES_INCO.map((a) => {
              const active = allergenes.includes(a.value);
              return (
                <button key={a.value} type="button" onClick={() => toggleAllergene(a.value)}
                  className={`rounded-full border px-3 py-1 text-xs transition ${active ? 'border-urgent bg-urgent/10 text-urgent' : 'border-hairline bg-paper text-soft hover:border-accent'}`}>
                  {a.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Cases d'activation des services — le cœur du "pas de double saisie" */}
        <Field label="Services souhaités" htmlFor="enf-services">
          <div id="enf-services" className="space-y-2">
            <ServiceCheckbox label="Cantine" icon={Utensils} checked={inscritCantine} onChange={setInscritCantine} />
            <ServiceCheckbox label="Périscolaire (garderie matin/soir)" icon={Clock} checked={inscritPeriscolaire} onChange={setInscritPeriscolaire} />
            <ServiceCheckbox label="Centre de loisirs (mercredis/vacances)" icon={Sun} checked={inscritAlsh} onChange={setInscritAlsh} />
            {petiteEnfanceActive && (
              <ServiceCheckbox label="Petite enfance (crèche, micro-crèche, accueil)" icon={Baby} checked={inscritPetiteEnfance} onChange={setInscritPetiteEnfance} />
            )}
          </div>
        </Field>

        {/* Champ vaccinal — uniquement si petite enfance cochée */}
        {petiteEnfanceActive && inscritPetiteEnfance && (
          <Field label="Vaccinations à jour" htmlFor="enf-vaccins">
            <select id="enf-vaccins" value={vaccins} onChange={(e) => setVaccins(e.target.value as '' | 'oui' | 'non')}
              className="w-full rounded-lg border border-hairline bg-paper px-3 py-2 text-sm text-ink">
              <option value="">Non renseigné (à compléter avec la mairie)</option>
              <option value="oui">Oui, à jour</option>
              <option value="non">Non / incomplètes</option>
            </select>
          </Field>
        )}
      </div>
    </Dialog>
  );
}

function ServiceCheckbox({ label, icon: Icon, checked, onChange }: {
  label: string; icon: typeof Utensils; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition ${
        checked ? 'border-accent bg-accent-wash text-ink' : 'border-hairline bg-paper text-soft hover:border-accent'
      }`}>
      <span className={`flex h-5 w-5 items-center justify-center rounded border ${checked ? 'border-accent bg-accent text-white' : 'border-hairline'}`}>
        {checked && <span className="text-xs">✓</span>}
      </span>
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

// ============================================================================
function InscriptionDialog({ open, enfantId, cantineOn, periOn, onClose, onCreated }: {
  open: boolean; enfantId: string; cantineOn: boolean; periOn: boolean; onClose: () => void; onCreated: () => void;
}) {
  const [service, setService] = useState<'cantine' | 'periscolaire'>(cantineOn ? 'cantine' : 'periscolaire');
  const [dateDebut, setDateDebut] = useState(''); const [dateFin, setDateFin] = useState('');
  const [jours, setJours] = useState<number[]>([1, 2, 4, 5]);
  const [creneaux, setCreneaux] = useState<('matin' | 'soir')[]>(['matin']);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [info, setInfo] = useState<string | null>(null);

  useEffect(() => { if (open) setService(cantineOn ? 'cantine' : 'periscolaire'); }, [open, cantineOn]);

  function toggleJour(iso: number) {
    setJours((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort());
  }
  function toggleCreneau(c: 'matin' | 'soir') {
    setCreneaux((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  }
  async function submit() {
    if (!dateDebut || !dateFin) { setErr('Renseignez la période.'); return; }
    if (dateFin < dateDebut) { setErr('La date de fin doit être après la date de début.'); return; }
    if (jours.length === 0) { setErr('Sélectionnez au moins un jour.'); return; }
    if (service === 'periscolaire' && creneaux.length === 0) { setErr('Choisissez au moins un créneau (matin / soir).'); return; }
    setBusy(true); setErr(null); setInfo(null);
    try {
      if (service === 'cantine') {
        const res = await api.scolaire.createInscription({ enfantId, dateDebut, dateFin, joursSemaine: jours });
        setInfo(`${res.reservationsCreees} repas réservé(s) sur la période.`);
      } else {
        const res = await api.scolaire.periscolaire.createInscription({ enfantId, dateDebut, dateFin, joursSemaine: jours, creneaux });
        setInfo(`${res.reservationsCreees} créneau(x) réservé(s) sur la période.`);
      }
      setTimeout(() => { onCreated(); }, 800);
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Erreur lors de l\'inscription'); }
    finally { setBusy(false); }
  }

  const titre = service === 'cantine' ? 'Inscrire à la cantine' : 'Inscrire au périscolaire';
  return (
    <Dialog open={open} onClose={onClose} title={titre}
      description="Choisissez le service, la période et les jours habituels. Les réservations seront créées sur les jours d'ouverture ; vous pourrez décocher les absences ensuite."
      footer={<><Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button><Button variant="cantine" onClick={submit} disabled={busy}>{busy ? 'Inscription…' : 'Inscrire'}</Button></>}>
      <div className="space-y-3">
        {err && <Alert variant="error">{err}</Alert>}
        {info && <Alert variant="success">{info}</Alert>}
        {cantineOn && periOn && (
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink">Service</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setService('cantine')}
                className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-3.5 text-sm font-medium transition-all',
                  service === 'cantine' ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                <Utensils className="h-4 w-4" /> Cantine
              </button>
              <button type="button" onClick={() => setService('periscolaire')}
                className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-3.5 text-sm font-medium transition-all',
                  service === 'periscolaire' ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                <Clock className="h-4 w-4" /> Périscolaire
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Du" htmlFor="ins-debut" required><Input id="ins-debut" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} /></Field>
          <Field label="Au" htmlFor="ins-fin" required><Input id="ins-fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} /></Field>
        </div>
        <Field label="Jours habituels" htmlFor="ins-jours">
          <div id="ins-jours" className="flex flex-wrap gap-1.5">
            {JOURS_LABELS.map((j) => {
              const active = jours.includes(j.iso);
              return (
                <button key={j.iso} type="button" onClick={() => toggleJour(j.iso)}
                  className={['inline-flex h-9 items-center rounded-r-pill px-3.5 text-sm font-medium transition-all',
                    active ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                  {j.label}
                </button>
              );
            })}
          </div>
        </Field>
        {service === 'periscolaire' && (
          <Field label="Créneaux" htmlFor="ins-creneaux">
            <div id="ins-creneaux" className="flex gap-1.5">
              {(['matin', 'soir'] as const).map((c) => {
                const on = creneaux.includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleCreneau(c)}
                    className={['inline-flex h-9 items-center gap-1.5 rounded-r-pill px-3.5 text-sm font-medium capitalize transition-all',
                      on ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                    {c === 'matin' ? <Sun className="h-4 w-4" /> : <Clock className="h-4 w-4" />} {c}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
      </div>
    </Dialog>
  );
}

// ============================================================================
/**
 * Encart dossier d'inscription scolaire (côté parent) + dépôt des pièces.
 */
function DossierInscriptionCard({ familleId, nbEnfants }: { familleId: string; nbEnfants: number }) {
  const [dossier, setDossier] = useState<DossierScolaireDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deplie, setDeplie] = useState(false);

  async function load() {
    try {
      const d = await api.scolaire.dossiers.getMien(familleId);
      setDossier(d);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [familleId]);

  async function soumettre() {
    setBusy(true); setError(null);
    try { await api.scolaire.dossiers.soumettre(familleId); await load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la soumission'); }
    finally { setBusy(false); }
  }

  if (loading) return null;

  const statut = dossier?.statut ?? 'brouillon';
  const config: Record<string, { titre: string; texte: string }> = {
    brouillon: { titre: 'Dossier non soumis', texte: 'Déclarez vos enfants, déposez les pièces demandées puis soumettez votre dossier.' },
    soumis: { titre: 'Dossier en cours de validation', texte: 'Votre dossier a été transmis à la mairie. Vous serez notifié dès qu\'il sera validé.' },
    valide: { titre: 'Dossier validé', texte: 'Vos enfants sont inscrits. Vous pouvez gérer la cantine et le périscolaire ci-dessous.' },
    refuse: { titre: 'Dossier à corriger', texte: dossier?.motifRefus ? `Motif : ${dossier.motifRefus}` : 'Votre dossier doit être corrigé puis soumis à nouveau.' },
  };
  const c = config[statut]!;
  const modifiable = statut === 'brouillon' || statut === 'refuse';
  // Quand le dossier est validé, on replie par défaut pour libérer la place
  // (le dossier ne sert qu'en début d'année) ; un clic sur l'en-tête le déplie.
  const estValide = statut === 'valide';
  const contenuVisible = !estValide || deplie;

  return (
    <Card className="mb-4 p-4">
      {error && <Alert variant="error" className="mb-3">{error}</Alert>}
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={() => estValide && setDeplie((v) => !v)}
          className={['flex items-center gap-2 text-left', estValide ? 'cursor-pointer' : 'cursor-default'].join(' ')}>
          <Badge>{c.titre}</Badge>
          {estValide && <span className="text-xs text-soft">{deplie ? '▲ masquer' : '▼ voir le dossier'}</span>}
        </button>
        {modifiable && (
          <Button variant="primary" size="sm" loading={busy} onClick={soumettre} disabled={nbEnfants === 0}>
            Soumettre mon dossier
          </Button>
        )}
      </div>

      {contenuVisible && (
        <>
          <p className="mt-2 text-sm text-soft">{c.texte}</p>

          {nbEnfants === 0 && modifiable && (
            <p className="mt-2 text-xs text-soft">Ajoutez au moins un enfant pour pouvoir soumettre.</p>
          )}
        </>
      )}
    </Card>
  );
}
